/** Atomic durable slots; the host owns IndexedDB, features own decoding and recovery. */
export interface AtomicIntentStore {
  read(key: string): Promise<string | null>
  reserve(
    key: string,
    candidate: string,
  ): Promise<{ value: string; created: boolean }>
  removeIfValue(key: string, expected: string): Promise<boolean>
  subscribe(key: string, listener: () => void): () => void
}
export interface OwnedAtomicIntentStore extends AtomicIntentStore {
  keys(): Promise<string[]>
  /** Device-only removal after credential retirement. Reuse the receipt's unique
   * operation ID on retry; a new login remains allowed to create fresh work. */
  forgetOwner(ownerId: string, operationId: string): Promise<number>
  /** Confirmed permanent account deletion only. The marker survives restarts;
   * ordinary sign-out and removable saved logins must not use this operation.
   * Retire the target session first: callers can already hold completed reads. */
  deleteOwner(ownerId: string): Promise<number>
}
export class IntentOwnerDeletedError extends Error {
  constructor() {
    super(
      'This account was deleted. Its saved requests are no longer available.',
    )
    this.name = 'IntentOwnerDeletedError'
  }
}
export class IntentOwnerForgottenError extends Error {
  constructor() {
    super(
      'This saved account was removed from the device. Reopen the record after signing in again.',
    )
    this.name = 'IntentOwnerForgottenError'
  }
}
export class IntentStorageError extends Error {
  constructor() {
    super(
      'Saved request storage is unavailable. Reopen the original record to recover.',
    )
    this.name = 'IntentStorageError'
  }
}
const buses = new Map<
  string,
  { listeners: Map<string, Set<() => void>>; channel?: BroadcastChannel }
>()
function busFor(name: string) {
  let bus = buses.get(name)
  if (!bus) {
    bus = { listeners: new Map() }
    buses.set(name, bus)
  }
  return bus
}
function notifyLocal(name: string, key: string) {
  for (const listener of buses.get(name)?.listeners.get(key) ?? []) {
    try {
      listener()
    } catch {
      /* Observers cannot reject a durable commit. */
    }
  }
}
function notify(name: string, key: string) {
  notifyLocal(name, key)
  try {
    buses.get(name)?.channel?.postMessage(key)
  } catch {
    /* Durable state does not depend on this notification. */
  }
}
/** Each factory can have its own connection. Atomicity belongs to the database,
 * not a process-local promise queue, storage event, clock or window identifier.
 */
export function createAtomicIntentStore(
  getFactory: () => IDBFactory = () => window.indexedDB,
  databaseName: string,
  storageError: () => Error = () => new IntentStorageError(),
  ownerForKey?: (key: string) => string | null,
  captureOwner?: (ownerId: string) => (() => Promise<void>) | undefined,
): OwnedAtomicIntentStore {
  // Array metadata keys cannot collide with the features' string slots.
  const deletionKey = (ownerId: string) => ['deleted-owner-v1', ownerId]
  const generationKey = (ownerId: string) => ['owner-generation-v1', ownerId]
  const forgottenKey = (ownerId: string, operationId: string) => [
    'forgotten-owner-operation-v1',
    ownerId,
    operationId,
  ]
  let connection: Promise<IDBDatabase> | undefined
  function open(): Promise<IDBDatabase> {
    if (connection) return connection
    const pending = new Promise<IDBDatabase>((resolve, reject) => {
      let rejected = false
      let request: IDBOpenDBRequest
      try {
        // Upgrade fences older clients that do not check device-removal generations.
        // Existing slots stay in place; old version-1/2 reopens fail closed.
        request = getFactory().open(databaseName, 3)
      } catch {
        reject(storageError())
        return
      }
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains('intents'))
          request.result.createObjectStore('intents')
      }
      const fail = () => {
        rejected = true
        reject(storageError())
      }
      request.onerror = fail
      request.onblocked = fail
      request.onsuccess = () => {
        const db = request.result
        if (rejected) {
          db.close()
          return
        }
        db.onclose = () => {
          connection = undefined
        }
        db.onversionchange = () => {
          db.close()
          connection = undefined
        }
        resolve(db)
      }
    })
    connection = pending
    void pending.catch(() => {
      if (connection === pending) connection = undefined
    })
    return pending
  }
  async function readGeneration(
    db: IDBDatabase,
    owner: string,
  ): Promise<string | null> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction('intents', 'readonly')
      const request = tx.objectStore('intents').get(generationKey(owner))
      tx.oncomplete = () => {
        if (request.result !== undefined && typeof request.result !== 'string')
          reject(storageError())
        else resolve(request.result ?? null)
      }
      tx.onabort = tx.onerror = () => reject(storageError())
    })
  }
  async function transaction<T>(
    key: string,
    mode: IDBTransactionMode,
    run: (
      store: IDBObjectStore,
      saved: string | null,
      result: (value: T) => void,
    ) => void,
  ): Promise<T> {
    // Capture the caller's login before the first await, then read the storage
    // generation before verifying credentials. The final transaction checks that
    // cleanup did not commit while the credential read was in progress.
    const owner = ownerForKey?.(key)
    const assertOwner = owner ? captureOwner?.(owner) : undefined
    let db: IDBDatabase
    let generation: string | null = null
    try {
      db = await open()
      if (owner) generation = await readGeneration(db, owner)
    } catch {
      throw storageError()
    }
    // Authorization failures retain their session error, never masquerade as a
    // storage failure that might encourage retrying under a replacement login.
    await assertOwner?.()
    try {
      return await new Promise<T>((resolve, reject) => {
        const tx = db.transaction('intents', mode, { durability: 'strict' })
        const store = tx.objectStore('intents')
        let result: T
        let supplied = false
        let failure: Error | undefined
        tx.oncomplete = () =>
          supplied ? resolve(result) : reject(storageError())
        tx.onabort = () => reject(failure ?? storageError())
        tx.onerror = () => reject(storageError())
        const readSlot = () => {
          const read = store.get(key)
          read.onsuccess = () => {
            try {
              if (
                read.result !== undefined &&
                typeof read.result !== 'string'
              ) {
                tx.abort()
                return
              }
              run(store, read.result ?? null, value => {
                result = value
                supplied = true
              })
            } catch {
              tx.abort()
            }
          }
        }
        const readCurrentGeneration = () => {
          const current = store.get(generationKey(owner!))
          current.onsuccess = () => {
            if ((current.result ?? null) !== generation) {
              failure = new IntentOwnerForgottenError()
              tx.abort()
            } else readSlot()
          }
        }
        if (owner) {
          const removed = store.get(deletionKey(owner))
          removed.onsuccess = () => {
            if (removed.result !== undefined) {
              failure = new IntentOwnerDeletedError()
              tx.abort()
            } else readCurrentGeneration()
          }
        } else readSlot()
      })
    } catch (error) {
      if (
        error instanceof IntentOwnerDeletedError ||
        error instanceof IntentOwnerForgottenError
      )
        throw error
      throw storageError()
    }
  }
  return {
    async keys() {
      try {
        const db = await open()
        return await new Promise<string[]>((resolve, reject) => {
          const tx = db.transaction('intents', 'readonly')
          const request = tx.objectStore('intents').getAllKeys()
          tx.oncomplete = () =>
            resolve(
              request.result.filter(
                (key): key is string => typeof key === 'string',
              ),
            )
          tx.onabort = () => reject(storageError())
          tx.onerror = () => reject(storageError())
        })
      } catch {
        throw storageError()
      }
    },
    async forgetOwner(ownerId, operationId) {
      if (!ownerForKey || !ownerId || !operationId || operationId.length > 128)
        throw storageError()
      let removed: string[]
      try {
        const db = await open()
        removed = await new Promise<string[]>((resolve, reject) => {
          const tx = db.transaction('intents', 'readwrite', {
            durability: 'strict',
          })
          const store = tx.objectStore('intents')
          const keys: string[] = []
          tx.oncomplete = () => resolve(keys)
          tx.onabort = tx.onerror = () => reject(storageError())
          const prior = store.get(forgottenKey(ownerId, operationId))
          prior.onsuccess = () => {
            // Keep the completed operation proof even after later removals. An
            // old receipt must never erase work created after a new login.
            if (prior.result !== undefined) {
              if (prior.result !== true) tx.abort()
              return
            }
            store.put(true, forgottenKey(ownerId, operationId))
            store.put(operationId, generationKey(ownerId))
            const cursor = store.openCursor()
            cursor.onsuccess = () => {
              const row = cursor.result
              if (!row) return
              try {
                if (
                  typeof row.key === 'string' &&
                  ownerForKey(row.key) === ownerId
                ) {
                  keys.push(row.key)
                  row.delete()
                }
                row.continue()
              } catch {
                tx.abort()
              }
            }
          }
        })
      } catch {
        throw storageError()
      }
      for (const key of removed) notify(databaseName, key)
      return removed.length
    },
    async deleteOwner(ownerId) {
      if (!ownerForKey || !ownerId) throw storageError()
      let deleted: string[]
      try {
        const db = await open()
        deleted = await new Promise<string[]>((resolve, reject) => {
          const tx = db.transaction('intents', 'readwrite', {
            durability: 'strict',
          })
          const store = tx.objectStore('intents')
          const keys: string[] = []
          tx.oncomplete = () => resolve(keys)
          tx.onabort = () => reject(storageError())
          tx.onerror = () => reject(storageError())
          // Marker and deletions commit together. A reserve in any connection
          // either precedes this transaction and is deleted, or sees the marker.
          store.put(true, deletionKey(ownerId))
          const request = store.openCursor()
          request.onsuccess = () => {
            const cursor = request.result
            if (!cursor) return
            try {
              if (
                typeof cursor.key === 'string' &&
                ownerForKey(cursor.key) === ownerId
              ) {
                keys.push(cursor.key)
                cursor.delete()
              }
              cursor.continue()
            } catch {
              tx.abort()
            }
          }
        })
      } catch {
        throw storageError()
      }
      for (const key of deleted) notify(databaseName, key)
      return deleted.length
    },
    read: key =>
      transaction(key, 'readonly', (_store, saved, done) => done(saved)),
    reserve: async (key, candidate) => {
      const result = await transaction<{ value: string; created: boolean }>(
        key,
        'readwrite',
        (store, saved, done) => {
          if (saved !== null) {
            done({ value: saved, created: false })
            return
          }
          store.add(candidate, key)
          done({ value: candidate, created: true })
        },
      )
      if (result.created) notify(databaseName, key)
      return result
    },
    removeIfValue: async (key, expected) => {
      const result = await transaction<boolean>(
        key,
        'readwrite',
        (store, saved, done) => {
          if (saved !== expected) {
            done(false)
            return
          }
          store.delete(key)
          done(true)
        },
      )
      if (result) notify(databaseName, key)
      return result
    },
    subscribe(key, listener) {
      const bus = busFor(databaseName)
      const listeners = bus.listeners.get(key) ?? new Set<() => void>()
      listeners.add(listener)
      bus.listeners.set(key, listeners)
      if (
        !bus.channel &&
        typeof window !== 'undefined' &&
        typeof window.BroadcastChannel === 'function'
      ) {
        try {
          bus.channel = new window.BroadcastChannel(
            `${databaseName}:changes:v1`,
          )
          bus.channel.onmessage = event => {
            if (typeof event.data === 'string' && event.data.length <= 2048)
              notifyLocal(databaseName, event.data)
          }
        } catch {
          /* A connection only improves cross-window visibility. */
        }
      }
      return () => {
        listeners.delete(listener)
        if (!listeners.size) bus.listeners.delete(key)
        if (!bus.listeners.size) {
          try {
            bus.channel?.close()
          } catch {
            /* Disposal cannot change durable state. */
          }
          bus.channel = undefined
          buses.delete(databaseName)
        }
      }
    },
  }
}
